import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  ChartBar,
  Check,
  Cpu,
  Sparkle,
  UsersThree,
} from '@phosphor-icons/react';

const BUILD_DURATION_MS = 10000;
const HEADLINE_INTERVAL_MS = 1700;
const WELCOME_INTERVAL_MS = 360;

const buildSteps = [
  {
    title: 'Personalized workspace',
    building: 'Built from your activation details',
    complete: 'Profile, layout, and preferences integrated',
    threshold: 20,
  },
  {
    title: 'FounderBoard benefits',
    building: 'Disclosed in order of impact',
    complete: 'Privileges unlocked and active',
    threshold: 45,
  },
  {
    title: 'First board setup',
    building: 'Ready for your first session',
    complete: 'Primary canvas structured & loaded',
    threshold: 72,
  },
  {
    title: 'Final stitching',
    building: 'Installing the last few features',
    complete: 'All micro-tools successfully loaded',
    threshold: 100,
  },
];

const loadingHeadlines = [
  'Reading your FounderBoard profile…',
  'Syncing goals, metrics, and preferences…',
  'Shaping your personalized workspace…',
  'Connecting boards and collaboration tools…',
  'Stitching together the final details…',
  'Almost there — preparing your launch…',
];

const welcomeWords = [
  'Welcome',
  'Bienvenue',
  'Bienvenido',
  'Willkommen',
  'Benvenuto',
  'Bem-vindo',
  'स्वागत है',
  'ようこそ',
  '환영합니다',
  'Welcome',
];

// A deliberately non-linear build curve. It moves quickly at the start,
// pauses slightly around meaningful milestones, and then finishes decisively.
// The total duration is still exactly BUILD_DURATION_MS.
const progressKeyframes = [
  [0.0, 0],
  [0.06, 5],
  [0.14, 14],
  [0.22, 23],
  [0.31, 31],
  [0.39, 36],
  [0.49, 47],
  [0.58, 59],
  [0.65, 67],
  [0.69, 69],
  [0.74, 71.5],
  [0.81, 78],
  [0.90, 88],
  [0.96, 96],
  [1.0, 100],
];

const smoothStep = (value) => value * value * (3 - 2 * value);

const getNaturalProgress = (elapsedRatio) => {
  const ratio = Math.max(0, Math.min(1, elapsedRatio));

  for (let i = 1; i < progressKeyframes.length; i += 1) {
    const [nextTime, nextProgress] = progressKeyframes[i];
    const [prevTime, prevProgress] = progressKeyframes[i - 1];

    if (ratio <= nextTime) {
      const localRatio = (ratio - prevTime) / (nextTime - prevTime);
      const eased = smoothStep(localRatio);
      return prevProgress + (nextProgress - prevProgress) * eased;
    }
  }

  return 100;
};

const celebrationParticles = Array.from({ length: 30 }, (_, index) => ({
  id: index,
  left: `${4 + ((index * 37) % 92)}%`,
  delay: `${(index % 10) * 0.11}s`,
  duration: `${2.4 + (index % 5) * 0.28}s`,
  drift: `${-42 + ((index * 29) % 84)}px`,
  size: `${5 + (index % 4) * 2}px`,
  rotation: `${80 + (index * 47) % 260}deg`,
  shape: index % 3,
}));

const BuildCheck = ({ complete }) => (
  <span
    className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border transition-all duration-300 ${
      complete
        ? 'border-[#2f9f95] bg-[#2f9f95] text-white scale-100'
        : 'border-[#c9ded8] bg-white text-[#43a69b] scale-95'
    }`}
  >
    {complete ? <Check size={12} weight="bold" /> : <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#43a69b]" />}
  </span>
);

const PreDashboardBuild = ({
  onContinue,
  continueLabel = 'Go to Dashboard',
  headlineSchedule = null,
  autoContinueDelayMs = null,
}) => {
  const [progress, setProgress] = useState(0);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [welcomeIndex, setWelcomeIndex] = useState(0);
  const [welcomeSequenceComplete, setWelcomeSequenceComplete] = useState(false);
  const [autoContinueSeconds, setAutoContinueSeconds] = useState(
    autoContinueDelayMs ? Math.ceil(autoContinueDelayMs / 1000) : null
  );

  useEffect(() => {
    const startedAt = performance.now();
    let rafId;

    const tick = (now) => {
      const elapsed = now - startedAt;
      const ratio = Math.min(1, elapsed / BUILD_DURATION_MS);
      setProgress(getNaturalProgress(ratio));

      if (ratio < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setProgress(100);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const complete = progress >= 100;

  useEffect(() => {
    if (complete) return undefined;

    if (headlineSchedule?.length) {
      setHeadlineIndex(0);
      const timeoutIds = [];
      let elapsed = 0;

      headlineSchedule.slice(0, -1).forEach((item, index) => {
        elapsed += item.durationMs;
        timeoutIds.push(window.setTimeout(() => setHeadlineIndex(index + 1), elapsed));
      });

      return () => timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    }

    const intervalId = window.setInterval(() => {
      setHeadlineIndex((current) => (current + 1) % loadingHeadlines.length);
    }, HEADLINE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [complete, headlineSchedule]);

  useEffect(() => {
    if (!complete) return undefined;

    setWelcomeIndex(0);
    setWelcomeSequenceComplete(false);
    let current = 0;
    const intervalId = window.setInterval(() => {
      current += 1;
      if (current >= welcomeWords.length) {
        window.clearInterval(intervalId);
        setWelcomeIndex(welcomeWords.length - 1);
        setWelcomeSequenceComplete(true);
        return;
      }
      setWelcomeIndex(current);
      if (current === welcomeWords.length - 1) {
        setWelcomeSequenceComplete(true);
        window.clearInterval(intervalId);
      }
    }, WELCOME_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [complete]);

  useEffect(() => {
    if (!complete || !welcomeSequenceComplete || !autoContinueDelayMs) return undefined;

    setAutoContinueSeconds(Math.ceil(autoContinueDelayMs / 1000));
    const startedAt = Date.now();
    const countdownId = window.setInterval(() => {
      const remaining = Math.max(0, autoContinueDelayMs - (Date.now() - startedAt));
      setAutoContinueSeconds(Math.ceil(remaining / 1000));
    }, 200);
    const continueId = window.setTimeout(() => {
      onContinue();
    }, autoContinueDelayMs);

    return () => {
      window.clearInterval(countdownId);
      window.clearTimeout(continueId);
    };
  }, [complete, welcomeSequenceComplete, autoContinueDelayMs, onContinue]);

  const roundedProgress = Math.floor(progress);
  const isNiceMoment = progress >= 68.5 && progress < 71.5;
  const completeCount = useMemo(
    () => buildSteps.filter((step) => progress >= step.threshold).length,
    [progress]
  );

  return (
    <div className="pd-shell relative overflow-hidden bg-[#f7f7f7] px-4 py-8 text-[#0f172a] sm:px-6 lg:px-8 lg:py-10">
      <style>{`
        /* Keep the pre-dashboard genuinely viewport-sized. Short laptop/browser
           viewports get progressively tighter spacing instead of forcing scroll. */
        .pd-shell { min-height: 100vh; min-height: 100dvh; }
        .pd-main { min-height: calc(100vh - 4rem); min-height: calc(100dvh - 4rem); }

        @media (max-height: 820px) and (min-width: 640px) {
          .pd-shell { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .pd-main { min-height: calc(100dvh - 2rem); }
          .pd-header { margin-bottom: 1rem !important; }
          .pd-brand { margin-bottom: .8rem !important; gap: .7rem !important; }
          .pd-brand img { width: 3rem !important; height: 3rem !important; border-radius: 11px !important; }
          .pd-brand-name { font-size: 1.7rem !important; }
          .pd-status-badge { margin-bottom: .45rem !important; }
          .pd-headline-wrap { min-height: 68px !important; }
          .pd-headline { font-size: 2rem !important; }
          .pd-description { margin-top: 0 !important; line-height: 1.25rem !important; }
          .pd-grid { gap: .75rem !important; }
          .pd-card { padding: 1rem !important; }
          .pd-progress-number { font-size: 2.1rem !important; }
          .pd-progress-block { margin-top: .65rem !important; }
          .pd-steps-wrap { margin-top: .7rem !important; padding-top: .55rem !important; }
          .pd-steps { gap: .45rem !important; }
          .pd-step-title { line-height: 1rem !important; }
          .pd-benefits-list { margin-top: .8rem !important; gap: .65rem !important; }
          .pd-welcome-pack { margin-top: .8rem !important; padding-top: .45rem !important; padding-bottom: .45rem !important; }
          .pd-footer { margin-top: .8rem !important; }
          .pd-continue { height: 44px !important; }
          .pd-footer-copy { margin-top: .25rem !important; }
        }

        @media (max-height: 700px) and (min-width: 640px) {
          .pd-shell { padding-top: .65rem !important; padding-bottom: .65rem !important; }
          .pd-main { min-height: calc(100dvh - 1.3rem); }
          .pd-header { margin-bottom: .6rem !important; }
          .pd-brand { margin-bottom: .35rem !important; }
          .pd-brand img { width: 2.5rem !important; height: 2.5rem !important; }
          .pd-brand-name { font-size: 1.45rem !important; }
          .pd-status-badge { margin-bottom: .2rem !important; }
          .pd-status-badge span { padding-top: .18rem !important; padding-bottom: .18rem !important; font-size: 9px !important; }
          .pd-headline-wrap { min-height: 52px !important; }
          .pd-headline { font-size: 1.65rem !important; }
          .pd-description { font-size: 12px !important; line-height: 1rem !important; }
          .pd-card { padding: .75rem !important; }
          .pd-progress-number { font-size: 1.8rem !important; }
          .pd-progress-block { margin-top: .45rem !important; }
          .pd-steps-wrap { margin-top: .45rem !important; padding-top: .4rem !important; }
          .pd-steps { gap: .25rem !important; }
          .pd-step-subtitle { display: none; }
          .pd-benefits-intro { display: none; }
          .pd-benefits-list { margin-top: .5rem !important; gap: .45rem !important; }
          .pd-welcome-pack { display: none !important; }
          .pd-footer { margin-top: .5rem !important; }
          .pd-continue { height: 40px !important; }
          .pd-footer-copy { display: none; }
        }

        @keyframes pd-confetti-fall {
          0% { transform: translate3d(0, -24px, 0) rotate(0deg); opacity: 0; }
          12% { opacity: 0.95; }
          100% { transform: translate3d(var(--drift), 105vh, 0) rotate(var(--rotation)); opacity: 0; }
        }
        @keyframes pd-celebration-pop {
          0% { transform: scale(0.7); opacity: 0; }
          55% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pd-welcome-roll {
          0% { transform: translateY(12px) rotateX(-55deg); opacity: 0; filter: blur(4px); }
          45% { transform: translateY(0) rotateX(0); opacity: 1; filter: blur(0); }
          100% { transform: translateY(-2px) rotateX(0); opacity: 1; filter: blur(0); }
        }
        @keyframes pd-headline-in {
          0% { transform: translateY(10px); opacity: 0; filter: blur(3px); }
          100% { transform: translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes pd-glow-pulse {
          0%, 100% { transform: scale(1); opacity: 0.45; }
          50% { transform: scale(1.08); opacity: 0.7; }
        }
      `}</style>

      <div
        className={`pointer-events-none absolute -left-24 -top-28 h-72 w-72 rounded-full bg-[#eafaf5] transition-opacity duration-700 ${complete ? 'opacity-70' : 'opacity-0'}`}
      />
      <div
        className={`pointer-events-none absolute -right-20 bottom-10 h-[420px] w-[420px] rounded-full bg-[#e8faf4] transition-opacity duration-700 ${complete ? 'opacity-70' : 'opacity-0'}`}
      />
      <div
        className={`pointer-events-none absolute left-[5%] top-[16%] h-80 w-80 rounded-full bg-[#effcf8] transition-opacity duration-700 ${complete ? 'opacity-60' : 'opacity-0'}`}
      />

      {complete && (
        <div className="pointer-events-none absolute inset-0 z-[2] overflow-hidden" aria-hidden="true">
          <div
            className="absolute left-1/2 top-[18%] h-72 w-72 -translate-x-1/2 rounded-full bg-[#b9f3df]/40 blur-3xl"
            style={{ animation: 'pd-glow-pulse 2.2s ease-in-out infinite' }}
          />
          {celebrationParticles.map((particle) => (
            <span
              key={particle.id}
              className={`absolute -top-5 ${
                particle.shape === 0
                  ? 'rounded-full bg-[#2fa497]'
                  : particle.shape === 1
                    ? 'rounded-sm bg-[#9adfca]'
                    : 'rounded-full bg-[#111b35]'
              }`}
              style={{
                left: particle.left,
                width: particle.size,
                height: particle.shape === 1 ? `${parseInt(particle.size, 10) * 1.7}px` : particle.size,
                animation: `pd-confetti-fall ${particle.duration} cubic-bezier(.2,.7,.35,1) ${particle.delay} both`,
                '--drift': particle.drift,
                '--rotation': particle.rotation,
              }}
            />
          ))}
        </div>
      )}

      <main className="pd-main relative z-10 mx-auto flex w-full max-w-[980px] flex-col justify-center">
        <header className="pd-header mb-7 text-center lg:mb-8">
          <div className="pd-brand mb-6 inline-flex items-center justify-center gap-4">
            <img
              src="/founderboard-logo.png"
              alt="FounderBoard"
              className="h-16 w-16 shrink-0 rounded-[14px] object-contain shadow-[0_7px_18px_rgba(15,23,42,0.12)]"
            />
            <span className="pd-brand-name font-heading text-[30px] font-extrabold leading-none tracking-[-0.7px] text-[#0f172a] sm:text-[34px]">
              FounderBoard
            </span>
          </div>

          {complete && (
            <div className="pd-status-badge mb-3 flex justify-center" style={{ animation: 'pd-celebration-pop 500ms ease-out both' }}>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a7efd2] bg-[#ecfff5] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#26988c]">
                <span>🚀</span> All systems ready
              </span>
            </div>
          )}

          <div className="pd-headline-wrap mx-auto flex min-h-[92px] max-w-[820px] items-center justify-center sm:min-h-[104px]">
            {complete ? (
              <h1 className="pd-headline font-heading text-[34px] font-extrabold leading-[1.08] tracking-[-0.035em] text-[#0f172a] sm:text-[42px] lg:text-[50px]">
                <span
                  key={welcomeIndex}
                  className="inline-block"
                  style={{ animation: 'pd-welcome-roll 330ms cubic-bezier(.2,.8,.2,1) both' }}
                >
                  {welcomeWords[welcomeIndex]}
                </span>{' '}
                to FounderBoard!
              </h1>
            ) : (
              <h1
                key={headlineIndex}
                className="pd-headline font-heading text-[31px] font-extrabold leading-[1.1] tracking-[-0.035em] text-[#172219] sm:text-[39px] lg:text-[46px]"
                style={{ animation: 'pd-headline-in 420ms cubic-bezier(.2,.8,.2,1) both' }}
              >
                {(headlineSchedule?.[headlineIndex]?.text) || loadingHeadlines[headlineIndex]}
              </h1>
            )}
          </div>

          <p className="pd-description mx-auto mt-1 max-w-[680px] text-sm leading-6 text-[#687267] sm:text-[15px]">
            {complete
              ? "We've completed stitching your profile, assets, and boards. Your fully personalized workspace has been assembled and is ready for takeoff."
              : 'We combine your profile, numbers, goals, preferences, and setup so your workspace feels complete and ready from the first open.'}
          </p>
        </header>

        <section className="pd-grid mx-auto grid w-full max-w-[760px] gap-4 lg:grid-cols-[1.18fr_0.82fr] lg:gap-5">
          <div className="pd-card rounded-[20px] border border-[#e1e7df] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.03)] sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#279d92]">
                  {complete ? 'Initialization status' : 'Installation progress'}
                </p>
                <div className="pd-progress-number mt-0.5 font-heading text-[42px] font-extrabold leading-none tracking-[-0.04em] text-[#172219]">
                  {roundedProgress}%
                </div>
              </div>
              <span className={`mt-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${complete ? 'border-[#9ae9c0] bg-[#dcfbea] text-[#238b78]' : 'border-[#bfeecf] bg-[#ecfff3] text-[#2c9a75]'}`}>
                {complete ? 'Ready to launch' : roundedProgress < 35 ? 'Building' : roundedProgress < 80 ? 'Almost ready' : 'Finalizing'}
              </span>
            </div>

            <div className="pd-progress-block mt-4">
              <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-[#687267]">
                <span>{complete ? 'Assembling assets & pipelines' : 'FounderBoard features'}</span>
                <span className="text-[#2d9f95]">{roundedProgress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[#e8efe8]">
                <div
                  className="h-full rounded-full bg-[#2d9f95]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="pd-steps-wrap mt-4 border-t border-[#eef2ed] pt-3">
              <div className="pd-steps space-y-3">
                {buildSteps.map((step) => {
                  const stepComplete = progress >= step.threshold;
                  return (
                    <div key={step.title} className={`flex items-start gap-3 transition-all duration-500 ${stepComplete ? 'translate-x-0 opacity-100' : 'translate-x-1 opacity-45'}`}>
                      <BuildCheck complete={stepComplete} />
                      <div className="min-w-0">
                        <p className="pd-step-title text-[14px] font-bold leading-5 text-[#2a302b]">{step.title}</p>
                        <p className="pd-step-subtitle text-[11px] leading-4 text-[#7d867f]">
                          {stepComplete ? step.complete : step.building}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={`pd-card hidden rounded-[20px] p-5 text-white shadow-[0_14px_32px_rgba(31,122,111,0.10)] transition-colors duration-500 sm:block sm:p-6 ${complete ? 'bg-gradient-to-br from-[#2d716a] to-[#34a99d]' : 'bg-[#34766f]'}`}>
            <p className="text-[12px] font-extrabold uppercase tracking-[0.08em] text-white/85">
              {complete ? "What's inside" : 'Benefits disclosed'}
            </p>
            <h2 className="mt-1 font-heading text-[18px] font-extrabold leading-tight">
              {complete ? 'Your Premium Sandbox' : 'Unlocking your workspace'}
            </h2>
            {!complete && (
              <p className="pd-benefits-intro mt-1 max-w-[220px] text-[12px] leading-5 text-white/70">
                Unlocked progressively as your workspace is assembled.
              </p>
            )}

            <div className="pd-benefits-list mt-6 space-y-4">
              {complete ? (
                finalFeatures.map(({ Icon, title, text }, index) => (
                  <div
                    key={title}
                    className="flex items-center gap-3"
                    style={{ animation: `pd-celebration-pop 420ms ease-out ${index * 120}ms both` }}
                  >
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white/15">
                      <Icon size={16} weight="bold" />
                    </span>
                    <div>
                      <p className="text-[13px] font-bold leading-4">{title}</p>
                      <p className="text-[10px] leading-4 text-white/70">{text}</p>
                    </div>
                  </div>
                ))
              ) : (
                buildSteps.map((step, index) => {
                  const stepComplete = progress >= step.threshold;
                  return (
                    <div key={step.title} className={`flex items-center gap-3 transition-all duration-500 ${stepComplete ? 'translate-x-0 opacity-100' : 'translate-x-1 opacity-35'}`}>
                      <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border ${stepComplete ? 'border-white/10 bg-white/15' : 'border-white/20 bg-transparent'}`}>
                        {stepComplete ? <Check size={14} weight="bold" /> : <span className="text-[10px] font-bold">{index + 1}</span>}
                      </span>
                      <div>
                        <p className="text-[12px] font-bold leading-4">{step.title}</p>
                        <p className="text-[10px] leading-4 text-white/65">{stepComplete ? step.complete : step.building}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {complete && (
              <div
                className="pd-welcome-pack mt-7 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2.5 text-[11px] font-semibold"
                style={{ animation: 'pd-celebration-pop 500ms ease-out 360ms both' }}
              >
                <Sparkle size={16} weight="fill" /> Welcome pack generated
              </div>
            )}
          </div>
        </section>

        <div className="pd-footer mx-auto mt-6 w-full max-w-[760px]">
          <button
            type="button"
            onClick={onContinue}
            disabled={!complete}
            className={`pd-continue flex h-[50px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all duration-300 ${
              complete
                ? 'bg-[#111b35] text-white shadow-[0_10px_26px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 hover:bg-[#17233f]'
                : 'cursor-not-allowed bg-[#9ba9bf] text-white'
            }`}
            data-testid="go-to-dashboard-button"
          >
            {continueLabel}
            {complete && welcomeSequenceComplete && autoContinueDelayMs ? ` (${autoContinueSeconds ?? Math.ceil(autoContinueDelayMs / 1000)})` : ''}
            {complete && <ArrowRight size={17} weight="bold" />}
          </button>
          <p className="pd-footer-copy mt-2 text-center text-[11px] text-[#a2abb9]">
            {complete
              ? autoContinueDelayMs
                ? welcomeSequenceComplete
                  ? 'Taking you to your dashboard automatically…'
                  : 'Completing your FounderBoard welcome…'
                : 'Workspace compiled successfully. Your dashboard is ready.'
              : isNiceMoment
                ? 'Finishing your setup… 😎😎😎 ...Nice.'
                : `Finishing your setup… ${completeCount} of ${buildSteps.length} workspace stages complete.`}
          </p>
        </div>
      </main>
    </div>
  );
};

const finalFeatures = [
  { Icon: Cpu, title: 'Founder AI', text: 'Ready to generate structures' },
  { Icon: ChartBar, title: 'Metrics Hub', text: 'Fully synced live datasets' },
  { Icon: UsersThree, title: 'Collaboration Sync', text: 'Shareable canvas tunnels active' },
];

export default PreDashboardBuild;
